import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  FiBold,
  FiItalic,
  FiUnderline,
  FiAlignLeft,
  FiAlignCenter,
  FiAlignRight,
  FiAlignJustify,
  FiSave,
  FiDownload,
  FiArrowLeft,
  FiChevronDown,
  FiList,
  FiType,
  FiFilm
} from 'react-icons/fi';
import { MdFormatColorText, MdFormatColorFill } from 'react-icons/md';
import { AiOutlineOrderedList } from 'react-icons/ai';
import { baseUrl } from '../config';

const ScriptEditor = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const editorRef = useRef(null);
  const isInitialMount = useRef(true);

  // Get script content from navigation state
  const { scriptContent, scriptId, conversationId, messageId, projectId } = location.state || {};



  const [content, setContent] = useState(scriptContent || '');
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [userSubscription, setUserSubscription] = useState('standard'); // 'standard' or 'premium'

  // Formatting states
  const [fontSize, setFontSize] = useState('16');
  const [fontFamily, setFontFamily] = useState('Dancing Script');
  const [showFontDropdown, setShowFontDropdown] = useState(false);
  const [showSizeDropdown, setShowSizeDropdown] = useState(false);
  const [textColor, setTextColor] = useState('#000000');
  const [bgColor, setBgColor] = useState('#ffff00');

  // Font options
  const fonts = [
    'Dancing Script',
    'Caveat',
    'Patrick Hand',
    'Shadows Into Light',
    'Arial',
    'Times New Roman',
    'Courier New',
    'Georgia',
    'Verdana'
  ];

  const fontSizes = ['10', '12', '14', '16', '18', '20', '24', '28', '32', '36', '48'];

  // Set initial content only once
  useEffect(() => {
    if (isInitialMount.current && editorRef.current && scriptContent) {
      editorRef.current.innerHTML = scriptContent;
      setContent(scriptContent);
      isInitialMount.current = false;
    }
  }, [scriptContent]);

  // Auto-save functionality
  useEffect(() => {
    if (!content || isInitialMount.current) return;

    const autoSaveTimer = setTimeout(() => {
      saveToBackend();
    }, 3000); // Auto-save after 3 seconds of inactivity

    return () => clearTimeout(autoSaveTimer);
  }, [content]);

  // Load user subscription status
  useEffect(() => {
    fetchUserSubscription();
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Check if Ctrl (Windows/Linux) or Cmd (Mac) is pressed
      const isMod = e.ctrlKey || e.metaKey;

      if (isMod) {
        switch (e.key.toLowerCase()) {
          case 'b':
            e.preventDefault();
            handleFormat('bold');
            break;
          case 'i':
            e.preventDefault();
            handleFormat('italic');
            break;
          case 'u':
            e.preventDefault();
            handleFormat('underline');
            break;
          case 's':
            e.preventDefault();
            saveToBackend();
            break;
          default:
            break;
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [content]);

  const fetchUserSubscription = async () => {
    try {
      const response = await fetch(`${baseUrl}/getUserSubscription`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setUserSubscription(data.subscription_type); // 'standard' or 'premium'
      }
    } catch (error) {
    }
  };

  const saveToBackend = async () => {
    setIsSaving(true);
    try {
      const response = await fetch(`${baseUrl}/saveScript`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
        },
        body: JSON.stringify({
          scriptId: scriptId,
          content: content,
          timestamp: new Date().toISOString(),
        }),
      });

      if (response.ok) {
        setLastSaved(new Date());
      }
    } catch (error) {
    } finally {
      setIsSaving(false);
    }
  };

  const handleFormat = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
  };

  const handleScriptBreakdown = () => {
    // Navigate to script breakdown page with current content
    navigate('/script-breakdown', {
      state: {
        scriptContent: content,
        scriptId: scriptId,
      }
    });
  };
  const handleBackToChat = () => {
    if (projectId) {
      // Navigate to script page with chatbot view
      navigate(`/input/${projectId}`, {
        state: {
          activePage: 2, // Go directly to chatbot page
          conversationId: conversationId,
          shouldLoadConversation: true
        }
      });
    } else {
      // Fallback to previous page
      navigate(-1);
    }
  };
  const handleDownloadPDF = async () => {
    if (userSubscription === 'standard') {
      alert('PDF download is available for Premium users only. Please upgrade your subscription!');
      return;
    }

    try {
      const response = await fetch(`${baseUrl}/downloadScriptPDF`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
        },
        body: JSON.stringify({
          scriptId: scriptId,
          content: content,
        }),
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `script_${scriptId}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error) {
      alert('Failed to download PDF. Please try again.');
    }
  };

  const handleContentChange = () => {
    if (editorRef.current) {
      setContent(editorRef.current.innerHTML);
    }
  };

  const formatLastSaved = () => {
    if (!lastSaved) return 'Not saved yet';
    const now = new Date();
    const diff = Math.floor((now - lastSaved) / 1000); // seconds
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
    return lastSaved.toLocaleTimeString();
  };

  return (
    <>
      <style>{`
        /* Google Fonts for Handwritten Styles */
        @import url('https://fonts.googleapis.com/css2?family=Dancing+Script:wght@400;700&family=Caveat:wght@400;700&family=Patrick+Hand&family=Shadows+Into+Light&display=swap');

        /* Main Container */
        .pb-scripteditor-container {
          display: flex;
          flex-direction: column;
          height: 100vh;
          width: 100%;
          background-color: #f5f5f5;
          overflow: hidden;
        }

        /* Top Navigation Bar */
        .pb-scripteditor-topnav {
          background: linear-gradient(135deg, #14b8a6 0%, #0d9488 100%);
          padding: 12px 24px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          flex-shrink: 0;
        }

        .pb-scripteditor-branding {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .pb-scripteditor-back-btn {
          background: rgba(255, 255, 255, 0.2);
          border: none;
          padding: 8px 12px;
          border-radius: 6px;
          color: white;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
          font-weight: 500;
          transition: all 0.2s;
        }

        .pb-scripteditor-back-btn:hover {
          background: rgba(255, 255, 255, 0.3);
        }

        .pb-scripteditor-logo {
          color: white;
          font-size: 20px;
          font-weight: 700;
        }

        .pb-scripteditor-actions {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .pb-scripteditor-save-status {
          color: white;
          font-size: 13px;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .pb-scripteditor-save-spinner {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: pb-scripteditor-spin 0.8s linear infinite;
        }

        @keyframes pb-scripteditor-spin {
          to { transform: rotate(360deg); }
        }

        .pb-scripteditor-download-btn {
          background: white;
          color: #14b8a6;
          border: none;
          padding: 8px 16px;
          border-radius: 6px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
          font-weight: 600;
          transition: all 0.2s;
        }

        .pb-scripteditor-download-btn:hover:not(:disabled) {
          background: #f0f0f0;
          transform: translateY(-1px);
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
        }

        .pb-scripteditor-download-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .pb-scripteditor-breakdown-btn {
          background: white;
          color: #6366f1;
          border: none;
          padding: 8px 16px;
          border-radius: 6px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
          font-weight: 600;
          transition: all 0.2s;
        }

        .pb-scripteditor-breakdown-btn:hover {
          background: #f0f0f0;
          transform: translateY(-1px);
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
        }

        .pb-scripteditor-premium-badge {
          background: #fbbf24;
          color: #78350f;
          font-size: 10px;
          padding: 2px 6px;
          border-radius: 4px;
          font-weight: 700;
          margin-left: 4px;
        }

        /* Toolbar */
        .pb-scripteditor-toolbar {
          background: white;
          border-bottom: 1px solid #e5e7eb;
          padding: 12px 24px;
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          align-items: center;
          flex-shrink: 0;
        }

        .pb-scripteditor-toolbar-group {
          display: flex;
          gap: 4px;
          padding-right: 12px;
          border-right: 1px solid #e5e7eb;
        }

        .pb-scripteditor-toolbar-group:last-child {
          border-right: none;
        }

        .pb-scripteditor-toolbar-btn {
          background: transparent;
          border: 1px solid transparent;
          padding: 8px 10px;
          border-radius: 4px;
          cursor: pointer;
          color: #374151;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .pb-scripteditor-toolbar-btn:hover {
          background: #f3f4f6;
          border-color: #d1d5db;
        }

        .pb-scripteditor-toolbar-btn:active {
          background: #e5e7eb;
        }

        /* Color Picker Styling */
        .pb-scripteditor-color-picker-wrapper {
          position: relative;
        }

        .pb-scripteditor-color-label {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 8px 10px;
          border-radius: 4px;
          cursor: pointer;
          border: 1px solid transparent;
          transition: all 0.2s;
          position: relative;
        }

        .pb-scripteditor-color-label:hover {
          background: #f3f4f6;
          border-color: #d1d5db;
        }

        .pb-scripteditor-color-icon {
          font-size: 20px;
          color: #374151;
          pointer-events: none;
        }

        .pb-scripteditor-color-input {
          position: absolute;
          width: 100%;
          height: 100%;
          top: 0;
          left: 0;
          opacity: 0;
          cursor: pointer;
        }

        /* Color indicator dot */
        .pb-scripteditor-color-label::after {
          content: '';
          position: absolute;
          bottom: 2px;
          left: 50%;
          transform: translateX(-50%);
          width: 20px;
          height: 3px;
          border-radius: 2px;
          background: var(--selected-color, currentColor);
        }

        /* Dropdown Buttons */
        .pb-scripteditor-dropdown {
          position: relative;
        }

        .pb-scripteditor-dropdown-btn {
          background: white;
          border: 1px solid #d1d5db;
          padding: 6px 12px;
          border-radius: 4px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
          min-width: 120px;
          justify-content: space-between;
        }

        .pb-scripteditor-dropdown-btn:hover {
          border-color: #9ca3af;
          background: #f9fafb;
        }

        .pb-scripteditor-dropdown-menu {
          position: absolute;
          top: 100%;
          left: 0;
          margin-top: 4px;
          background: white;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          max-height: 300px;
          overflow-y: auto;
          z-index: 1000;
          min-width: 180px;
        }

        .pb-scripteditor-dropdown-item {
          padding: 10px 16px;
          cursor: pointer;
          transition: background 0.2s;
          font-size: 14px;
        }

        .pb-scripteditor-dropdown-item:hover {
          background: #f3f4f6;
        }

        .pb-scripteditor-font-preview {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        /* Editor Area */
        .pb-scripteditor-editor-wrapper {
          flex: 1;
          overflow: hidden;
          display: flex;
          justify-content: center;
          padding: 24px;
          background: #e5e7eb;
        }

        .pb-scripteditor-paper {
          background: white;
          width: 100%;
          max-width: 8.5in;
          min-height: 11in;
          padding: 1in;
          box-shadow: 0 0 20px rgba(0, 0, 0, 0.1);
          overflow-y: auto;
        }

        .pb-scripteditor-content {
          min-height: 100%;
          outline: none;
          font-size: 16px;
          line-height: 1.8;
          color: #1f2937;
          font-family: 'Dancing Script', cursive;
        }

        .pb-scripteditor-content:empty:before {
          content: 'Start writing your script here...';
          color: #9ca3af;
          font-style: italic;
        }

        /* Status Bar */
        .pb-scripteditor-statusbar {
          background: white;
          border-top: 1px solid #e5e7eb;
          padding: 8px 24px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 12px;
          color: #6b7280;
          flex-shrink: 0;
        }

        .pb-scripteditor-word-count {
          display: flex;
          gap: 16px;
        }

        /* Scrollbar Styling */
        .pb-scripteditor-paper::-webkit-scrollbar {
          width: 8px;
        }

        .pb-scripteditor-paper::-webkit-scrollbar-track {
          background: #f1f1f1;
        }

        .pb-scripteditor-paper::-webkit-scrollbar-thumb {
          background: #14b8a6;
          border-radius: 4px;
        }

        .pb-scripteditor-paper::-webkit-scrollbar-thumb:hover {
          background: #0d9488;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .pb-scripteditor-paper {
            padding: 0.5in;
          }

          .pb-scripteditor-toolbar {
            padding: 8px 16px;
          }

          .pb-scripteditor-toolbar-group {
            padding-right: 8px;
          }
        }
      `}</style>

      <div className="pb-scripteditor-container">
        {/* Top Navigation */}
        <div className="pb-scripteditor-topnav">
          <div className="pb-scripteditor-branding">
           <button
              className="pb-scripteditor-back-btn"
              onClick={handleBackToChat}  // ✅ CHANGED
            >
              <FiArrowLeft /> Back to Chat
            </button>
            <h1 className="pb-scripteditor-logo">Producer Buddy - Script Editor</h1>
          </div>

          <div className="pb-scripteditor-actions">
            <div className="pb-scripteditor-save-status">
              {isSaving ? (
                <>
                  <div className="pb-scripteditor-save-spinner"></div>
                  Saving...
                </>
              ) : (
                <>
                  <FiSave />
                  {formatLastSaved()}
                </>
              )}
            </div>

            <button
              className="pb-scripteditor-breakdown-btn"
              onClick={handleScriptBreakdown}
              title="Break down script into scenes"
            >
              <FiFilm />
              Script Breakdown
            </button>

            <button
              className="pb-scripteditor-download-btn"
              onClick={handleDownloadPDF}
              disabled={userSubscription === 'standard'}
              title={userSubscription === 'standard' ? 'Premium feature - Upgrade to download' : 'Download as PDF'}
            >
              <FiDownload />
              Download PDF
              {userSubscription === 'standard' && (
                <span className="pb-scripteditor-premium-badge">PREMIUM</span>
              )}
            </button>
          </div>
        </div>

        {/* Toolbar */}
        <div className="pb-scripteditor-toolbar">
          {/* Font Family */}
          <div className="pb-scripteditor-dropdown">
            <button
              className="pb-scripteditor-dropdown-btn"
              onClick={() => setShowFontDropdown(!showFontDropdown)}
            >
              <span style={{ fontFamily: fontFamily }}>{fontFamily}</span>
              <FiChevronDown />
            </button>
            {showFontDropdown && (
              <div className="pb-scripteditor-dropdown-menu">
                {fonts.map((font) => (
                  <div
                    key={font}
                    className="pb-scripteditor-dropdown-item"
                    onClick={() => {
                      setFontFamily(font);
                      handleFormat('fontName', font);
                      setShowFontDropdown(false);
                    }}
                  >
                    <span style={{ fontFamily: font }}>{font}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Font Size */}
          <div className="pb-scripteditor-dropdown">
            <button
              className="pb-scripteditor-dropdown-btn"
              onClick={() => setShowSizeDropdown(!showSizeDropdown)}
              style={{ minWidth: '80px' }}
            >
              <span>{fontSize}px</span>
              <FiChevronDown />
            </button>
            {showSizeDropdown && (
              <div className="pb-scripteditor-dropdown-menu">
                {fontSizes.map((size) => (
                  <div
                    key={size}
                    className="pb-scripteditor-dropdown-item"
                    onClick={() => {
                      setFontSize(size);
                      handleFormat('fontSize', size);
                      setShowSizeDropdown(false);
                    }}
                  >
                    {size}px
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Text Formatting */}
          <div className="pb-scripteditor-toolbar-group">
            <button
              className="pb-scripteditor-toolbar-btn"
              onClick={() => handleFormat('bold')}
              title="Bold (Ctrl+B)"
            >
              <FiBold />
            </button>
            <button
              className="pb-scripteditor-toolbar-btn"
              onClick={() => handleFormat('italic')}
              title="Italic (Ctrl+I)"
            >
              <FiItalic />
            </button>
            <button
              className="pb-scripteditor-toolbar-btn"
              onClick={() => handleFormat('underline')}
              title="Underline (Ctrl+U)"
            >
              <FiUnderline />
            </button>
          </div>

          {/* Text Alignment */}
          <div className="pb-scripteditor-toolbar-group">
            <button
              className="pb-scripteditor-toolbar-btn"
              onClick={() => handleFormat('justifyLeft')}
              title="Align Left"
            >
              <FiAlignLeft />
            </button>
            <button
              className="pb-scripteditor-toolbar-btn"
              onClick={() => handleFormat('justifyCenter')}
              title="Align Center"
            >
              <FiAlignCenter />
            </button>
            <button
              className="pb-scripteditor-toolbar-btn"
              onClick={() => handleFormat('justifyRight')}
              title="Align Right"
            >
              <FiAlignRight />
            </button>
            <button
              className="pb-scripteditor-toolbar-btn"
              onClick={() => handleFormat('justifyFull')}
              title="Justify"
            >
              <FiAlignJustify />
            </button>
          </div>

          {/* Lists */}
          <div className="pb-scripteditor-toolbar-group">
            <button
              className="pb-scripteditor-toolbar-btn"
              onClick={() => handleFormat('insertUnorderedList')}
              title="Bullet List"
            >
              <FiList />
            </button>
            <button
              className="pb-scripteditor-toolbar-btn"
              onClick={() => handleFormat('insertOrderedList')}
              title="Numbered List"
            >
              <AiOutlineOrderedList />
            </button>
          </div>

          {/* Colors */}
          <div className="pb-scripteditor-toolbar-group">
            <div className="pb-scripteditor-color-picker-wrapper">
              <label
                className="pb-scripteditor-color-label"
                title="Text Color"
                style={{ '--selected-color': textColor }}
              >
                <MdFormatColorText className="pb-scripteditor-color-icon" />
                <input
                  type="color"
                  value={textColor}
                  onChange={(e) => {
                    setTextColor(e.target.value);
                    handleFormat('foreColor', e.target.value);
                  }}
                  className="pb-scripteditor-color-input"
                />
              </label>
            </div>
            <div className="pb-scripteditor-color-picker-wrapper">
              <label
                className="pb-scripteditor-color-label"
                title="Background Highlight"
                style={{ '--selected-color': bgColor }}
              >
                <MdFormatColorFill className="pb-scripteditor-color-icon" />
                <input
                  type="color"
                  value={bgColor}
                  onChange={(e) => {
                    setBgColor(e.target.value);
                    handleFormat('hiliteColor', e.target.value);
                  }}
                  className="pb-scripteditor-color-input"
                />
              </label>
            </div>
          </div>
        </div>

        {/* Editor Area */}
        <div className="pb-scripteditor-editor-wrapper">
          <div className="pb-scripteditor-paper">
            <div
              ref={editorRef}
              className="pb-scripteditor-content"
              contentEditable
              onInput={handleContentChange}
              suppressContentEditableWarning
              style={{
                fontFamily: fontFamily,
                fontSize: `${fontSize}px`,
              }}
            />
          </div>
        </div>

        {/* Status Bar */}
        <div className="pb-scripteditor-statusbar">
          <div className="pb-scripteditor-word-count">
            <span>Words: {content.replace(/<[^>]*>/g, '').trim().split(/\s+/).length}</span>
            <span>Characters: {content.replace(/<[^>]*>/g, '').length}</span>
          </div>
          <div>
            {userSubscription === 'premium' ? (
              <span style={{ color: '#fbbf24', fontWeight: 600 }}>⭐ Premium User</span>
            ) : (
              <span>Standard User - Upgrade for PDF download</span>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

ScriptEditor.propTypes = {
  // Props passed via navigation state
};

export default ScriptEditor;
